import { gql } from "@apollo/client";
import { CompanyMatchFilter } from "../../legacy/types/companyFilter";
import { GlobalSettings } from "../../types/globalSettings";

export const ICP_INDUSTRY_DISTRIBUTION_QUERY = gql`
  query icpIndustryDistribution(
    $productIds: [Int!]
    $matchFile: CompanyMatchFilter
    $globalSettings: GlobalSettings
  ) {
    icpIndustryDistribution(
      productIds: $productIds
      matchFile: $matchFile
      globalSettings: $globalSettings
    ) {
      share
      industryId
      isSignificant
      count
    }
  }
`;

export const ICP_GEOGRAPHY_DISTRIBUTION_QUERY = gql`
  query icpGeographyDistribution(
    $productIds: [Int!]
    $matchFile: CompanyMatchFilter
    $globalSettings: GlobalSettings
  ) {
    icpGeographyDistribution(
      productIds: $productIds
      matchFile: $matchFile
      globalSettings: $globalSettings
    ) {
      share
      countryCode
      isSignificant
      count
    }
  }
`;

export const ICP_REVENUE_DISTRIBUTION_QUERY = gql`
  query icpRevenueDistribution(
    $productIds: [Int!]
    $matchFile: CompanyMatchFilter
    $globalSettings: GlobalSettings
  ) {
    icpRevenueDistribution(
      productIds: $productIds
      matchFile: $matchFile
      globalSettings: $globalSettings
    ) {
      share
      count
      isSignificant
      revenueRange
      revenueRangeMax
      revenueRangeMin
    }
  }
`;

export const ICP_EMPLOYEE_COUNT_DISTRIBUTION_QUERY = gql`
  query icpEmployeeCountDistribution(
    $productIds: [Int!]
    $matchFile: CompanyMatchFilter
    $globalSettings: GlobalSettings
  ) {
    icpEmployeeCountDistribution(
      productIds: $productIds
      matchFile: $matchFile
      globalSettings: $globalSettings
    ) {
      share
      count
      isSignificant
      employeesRange
      employeesRangeMin
      employeesRangeMax
    }
  }
`;

export const ICP_WIN_LOSS_INDUSTRY_DISTRIBUTION_QUERY = gql`
  query icpWinLossIndustryDistribution(
    $matchFile: CompanyMatchFilter!
    $globalSettings: GlobalSettings
  ) {
    icpWinLossIndustryDistribution(
      matchFile: $matchFile
      globalSettings: $globalSettings
    ) {
      industryId
      isSignificant
      countWon
      countLost
      totalCount
      minSampleSize
    }
  }
`;

export const ICP_GEOGRAPHY_WIN_LOSS_DISTRIBUTION_QUERY = gql`
  query icpGeographyWinLossDistribution(
    $matchFile: CompanyMatchFilter!
    $globalSettings: GlobalSettings
  ) {
    icpGeographyWinLossDistribution(
      matchFile: $matchFile
      globalSettings: $globalSettings
    ) {
      countryCode
      isSignificant
      countWon
      countLost
      totalCount
      minSampleSize
    }
  }
`;

export const ICP_REVENUE_WIN_LOSS_DISTRIBUTION_QUERY = gql`
  query icpRevenueWinLossDistribution(
    $matchFile: CompanyMatchFilter!
    $globalSettings: GlobalSettings
  ) {
    icpRevenueWinLossDistribution(
      matchFile: $matchFile
      globalSettings: $globalSettings
    ) {
      revenueRange
      revenueRangeMin
      revenueRangeMax
      isSignificant
      countWon
      countLost
      totalCount
      minSampleSize
    }
  }
`;

export const ICP_EMPLOYEE_COUNT_WIN_LOSS_DISTRIBUTION_QUERY = gql`
  query icpEmployeeCountWinLossDistribution(
    $matchFile: CompanyMatchFilter!
    $globalSettings: GlobalSettings
  ) {
    icpEmployeeCountWinLossDistribution(
      matchFile: $matchFile
      globalSettings: $globalSettings
    ) {
      employeesRange
      employeesRangeMin
      employeesRangeMax
      isSignificant
      countWon
      countLost
      totalCount
      minSampleSize
    }
  }
`;

export interface ICPIndustryDistribution {
  share: number;
  industryId: number;
  isSignificant: boolean;
  count: number;
}

export interface ICPGeographyDistribution {
  share: number;
  countryCode: string;
  isSignificant: boolean;
  count: number;
}

export interface ICPRevenueDistribution {
  share: number;
  count: number;
  isSignificant: boolean;
  revenueRange: string;
  revenueRangeMax: number | null;
  revenueRangeMin: number | null;
}

export interface ICPEmployeeCountDistribution {
  share: number;
  count: number;
  isSignificant: boolean;
  employeesRange: string;
  employeesRangeMin: number | null;
  employeesRangeMax: number | null;
}

export interface ICPWinLossIndustryDistribution {
  industryId: number;
  isSignificant: boolean | number; // API might return 0/1 instead of true/false
  countWon: number;
  countLost: number;
  totalCount: number;
  minSampleSize: number;
}

export interface ICPGeographyWinLossDistribution {
  countryCode: string;
  isSignificant: boolean | number;
  countWon: number;
  countLost: number;
  totalCount: number;
  minSampleSize: number;
}

export interface ICPRevenueWinLossDistribution {
  revenueRange: string;
  revenueRangeMin: number | null;
  revenueRangeMax: number | null;
  isSignificant: boolean | number;
  countWon: number;
  countLost: number;
  totalCount: number;
  minSampleSize: number;
}

export interface ICPEmployeeCountWinLossDistribution {
  employeesRange: string;
  employeesRangeMin: number | null;
  employeesRangeMax: number | null;
  isSignificant: boolean | number;
  countWon: number;
  countLost: number;
  totalCount: number;
  minSampleSize: number;
}

// Query payload interfaces
export interface ICPIndustryDistributionPayload {
  icpIndustryDistribution: ICPIndustryDistribution[];
}

export interface ICPGeographyDistributionPayload {
  icpGeographyDistribution: ICPGeographyDistribution[];
}

export interface ICPRevenueDistributionPayload {
  icpRevenueDistribution: ICPRevenueDistribution[];
}

export interface ICPEmployeeCountDistributionPayload {
  icpEmployeeCountDistribution: ICPEmployeeCountDistribution[];
}

export interface ICPWinLossIndustryDistributionPayload {
  icpWinLossIndustryDistribution: ICPWinLossIndustryDistribution[];
}

export interface ICPGeographyWinLossDistributionPayload {
  icpGeographyWinLossDistribution: ICPGeographyWinLossDistribution[];
}

export interface ICPRevenueWinLossDistributionPayload {
  icpRevenueWinLossDistribution: ICPRevenueWinLossDistribution[];
}

export interface ICPEmployeeCountWinLossDistributionPayload {
  icpEmployeeCountWinLossDistribution: ICPEmployeeCountWinLossDistribution[];
}

export interface ICPDistributionVariables {
  productIds?: number[];
  matchFile?: CompanyMatchFilter;
  globalSettings?: GlobalSettings;
}
